import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapPin, ArrowUpDown, ChevronRight } from 'lucide-react';
import { api } from '../lib/api';
import AdminDemographicsMap from './AdminDemographicsMap';
import AdminDemographicsActiveUsersModal from './AdminDemographicsActiveUsersModal';

const SORT_FIELDS = { state: 'state', count: 'count' };
const UNKNOWN = 'Unknown';

const AdminDemographicsSection = ({ onSelectUser }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortField, setSortField] = useState(SORT_FIELDS.count);
  const [sortDir, setSortDir] = useState('desc');
  const [expanded, setExpanded] = useState(() => new Set());
  // Active-today users modal: { title, users } when open, null when closed.
  const [activeModal, setActiveModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: payload } = await api.getAdminDemographics();
      setData(payload);
    } catch (err) {
      setError('Could not load demographics.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === SORT_FIELDS.count ? 'desc' : 'asc');
    }
  };

  const toggleExpand = (state) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(state)) next.delete(state);
      else next.add(state);
      return next;
    });
  };

  // Open the active-today users popup for a whole state (city omitted) or a
  // single city. Filters the activeUsers list already returned by the
  // demographics endpoint — no extra request needed.
  const openActiveUsers = (state, city) => {
    const all = data?.activeUsers || [];
    const users = all.filter(
      (u) => (u.state || UNKNOWN) === state && (city == null || (u.city || UNKNOWN) === city),
    );
    setActiveModal({
      title: city ? `${state} · ${city}` : state,
      users,
    });
  };

  // A user tapped in the popup: reuse the same profile navigation as Top
  // Referrers / Search users (parent opens AdminUserProfileModal).
  const handleSelectUser = (userId) => {
    setActiveModal(null);
    if (userId) onSelectUser?.(userId);
  };

  // Group city-level rows into a single collapsed row per state, carrying the
  // total user count for that state plus its city-wise breakdown.
  const stateGroups = useMemo(() => {
    const byState = new Map();
    for (const row of data?.cities || []) {
      const state = row.state || UNKNOWN;
      if (!byState.has(state)) byState.set(state, { state, count: 0, activeCount: 0, cities: [] });
      const group = byState.get(state);
      group.count += row.count;
      group.activeCount += row.activeCount || 0;
      group.cities.push({ city: row.city || UNKNOWN, count: row.count, activeCount: row.activeCount || 0 });
    }

    const groups = [...byState.values()];
    const dir = sortDir === 'asc' ? 1 : -1;
    groups.sort((a, b) => {
      // Unknown always sinks to the bottom regardless of count or direction.
      if (a.state === UNKNOWN && b.state !== UNKNOWN) return 1;
      if (b.state === UNKNOWN && a.state !== UNKNOWN) return -1;
      if (sortField === SORT_FIELDS.count) return (a.count - b.count) * dir;
      return a.state.toLowerCase().localeCompare(b.state.toLowerCase()) * dir;
    });

    // City breakdown within a state is always sorted by count, highest first.
    for (const group of groups) {
      group.cities.sort((a, b) => b.count - a.count || a.city.localeCompare(b.city));
    }
    return groups;
  }, [data, sortField, sortDir]);

  const points = data?.points || [];

  return (
    <div className="rounded-[12px] border border-[#ececec] bg-white p-3 space-y-3" data-testid="admin-demographics-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#9333EA]" />
          <h3 className="text-sm font-bold text-[#111111] font-['Outfit']">Demographics</h3>
        </div>
        <button
          onClick={load}
          className="h-8 rounded-[9px] bg-[#111111] px-3 text-white text-xs font-semibold"
          data-testid="admin-demographics-refresh"
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && <p className="text-xs text-red-600 font-['DM_Sans']">{error}</p>}

      {data && !data.geoipAvailable && (
        <p className="text-xs text-amber-600 font-['DM_Sans']">
          Location database not loaded on the server yet — counts will populate after the next deploy and backfill.
        </p>
      )}

      {data && (
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-[10px] bg-[#fafafa] border border-[#ececec] p-2 text-center">
            <p className="text-[10px] text-[#666666]">Located</p>
            <p className="text-sm font-bold text-[#111111]">{data.locatedUsers}</p>
          </div>
          <div className="rounded-[10px] bg-[#fafafa] border border-[#ececec] p-2 text-center">
            <p className="text-[10px] text-[#666666]">Unlocated</p>
            <p className="text-sm font-bold text-[#111111]">{data.unlocatedUsers}</p>
          </div>
          <div className="rounded-[10px] bg-[#fafafa] border border-[#ececec] p-2 text-center">
            <p className="text-[10px] text-[#666666]">Total Users</p>
            <p className="text-sm font-bold text-[#111111]">{data.totalUsers}</p>
          </div>
        </div>
      )}

      <AdminDemographicsMap points={points} />

      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="text-[#666666]">
              <th>
                <button
                  onClick={() => toggleSort(SORT_FIELDS.state)}
                  className={`inline-flex items-center gap-1 py-2 font-semibold hover:text-[#111111] ${sortField === SORT_FIELDS.state ? 'text-[#9333EA]' : ''}`}
                  data-testid="admin-demographics-sort-state"
                >
                  State
                  <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>
              <th className="text-right">
                <button
                  onClick={() => toggleSort(SORT_FIELDS.count)}
                  className={`inline-flex items-center gap-1 py-2 font-semibold hover:text-[#111111] ${sortField === SORT_FIELDS.count ? 'text-[#9333EA]' : ''}`}
                  data-testid="admin-demographics-sort-count"
                >
                  Users
                  <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {stateGroups.map((group) => {
              const isOpen = expanded.has(group.state);
              return (
                <React.Fragment key={group.state}>
                  <tr
                    onClick={() => toggleExpand(group.state)}
                    className="border-t border-[#f0f0f0] cursor-pointer hover:bg-[#fafafa]"
                    data-testid={`admin-demographics-state-row-${group.state}`}
                  >
                    <td className="py-2 text-[#111111]">
                      <span className="inline-flex items-center gap-1">
                        <ChevronRight
                          className={`w-3.5 h-3.5 text-[#666666] transition-transform ${isOpen ? 'rotate-90' : ''}`}
                        />
                        <span className="font-semibold">{group.state}</span>
                        <span className="text-[#999999]">({group.cities.length})</span>
                      </span>
                    </td>
                    <td className="py-2 text-right font-semibold">
                      {group.activeCount > 0 ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openActiveUsers(group.state);
                          }}
                          className="text-green-600 underline decoration-dotted underline-offset-2 hover:text-green-700"
                          title="View active-today users"
                          data-testid={`admin-demographics-state-active-${group.state}`}
                        >
                          {group.activeCount}
                        </button>
                      ) : (
                        <span
                          className="text-green-600"
                          title="Active today"
                          data-testid={`admin-demographics-state-active-${group.state}`}
                        >
                          {group.activeCount}
                        </span>
                      )}
                      <span className="ml-2 text-[#111111]">{group.count}</span>
                    </td>
                  </tr>
                  {isOpen && group.cities.map((c, i) => (
                    <tr
                      key={`${group.state}-${c.city}-${i}`}
                      className="border-t border-[#f6f6f6] bg-[#fafafa]"
                      data-testid={`admin-demographics-city-row-${group.state}-${c.city}`}
                    >
                      <td className="py-1.5 pl-6 text-[#666666]">{c.city}</td>
                      <td className="py-1.5 text-right">
                        {c.activeCount > 0 ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openActiveUsers(group.state, c.city);
                            }}
                            className="text-green-600 underline decoration-dotted underline-offset-2 hover:text-green-700"
                            title="View active-today users"
                            data-testid={`admin-demographics-city-active-${group.state}-${c.city}`}
                          >
                            {c.activeCount}
                          </button>
                        ) : (
                          <span
                            className="text-green-600"
                            title="Active today"
                            data-testid={`admin-demographics-city-active-${group.state}-${c.city}`}
                          >
                            {c.activeCount}
                          </span>
                        )}
                        <span className="ml-2 text-[#666666]">{c.count}</span>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            {!loading && stateGroups.length === 0 && (
              <tr>
                <td colSpan={2} className="py-4 text-center text-[#666666]">No location data yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AdminDemographicsActiveUsersModal
        isOpen={Boolean(activeModal)}
        onClose={() => setActiveModal(null)}
        title={activeModal?.title || ''}
        users={activeModal?.users || []}
        onSelectUser={handleSelectUser}
      />
    </div>
  );
};

export default AdminDemographicsSection;
